/**
 * Alert Engine — Prompt.md §18, §20, §21.
 *
 * Nhận `PostureEvent` (đã qua toàn bộ luật thời gian của §15) và quyết định **có nói gì không**.
 * Không biết Web Speech, không đọc giờ hệ thống, không giữ timer: `now` được bơm vào từ ngoài để
 * mọi luật cooldown test được bằng số nguyên thay vì bằng `setTimeout`.
 *
 * `decide()` **không** thay đổi trạng thái. Chỉ `handle()` mới ghi nhận cooldown — nhờ vậy Debug
 * Mode gọi `decide()` bao nhiêu lần cũng được để hiển thị "đang bị chặn vì lý do gì" mà không âm
 * thầm tiêu mất một suất cảnh báo thật.
 */

import type { AlertConfig } from "@studystraight/shared-config";
import type {
  AlertDecision,
  AlertMessageKey,
  AlertRecord,
  EmittedSeverity,
  PostureEvent,
  PostureIssueType,
  SuppressedAlert,
} from "@studystraight/shared-types";

import { silentAlertProvider, type AlertProvider } from "./AlertProvider.js";
import { messageFor, messageKeyFor } from "./messageSelection.js";

const SEVERITY_RANK: Record<EmittedSeverity, number> = { WARNING: 1, BAD: 2 };

/** Không có gì để xét — khác với "bị chặn": không kèm `suppressedReason`. */
const NOTHING: AlertDecision = { emit: false };

interface Selection {
  decision: AlertDecision;
  event?: PostureEvent;
}

export class AlertEngine {
  private muted = false;
  private lastAlertAt: number | null = null;
  private readonly lastIssueAt = new Map<PostureIssueType, number>();
  private readonly history: AlertRecord[] = [];
  private unavailableIssues: ReadonlySet<PostureIssueType> = new Set();

  constructor(
    private readonly config: AlertConfig,
    private readonly provider: AlertProvider = silentAlertProvider,
  ) {}

  /** Số cảnh báo đã phát trong phiên — §22 dùng cho `alertCount`. */
  get alertCount(): number {
    return this.history.length;
  }

  get alerts(): readonly AlertRecord[] {
    return this.history;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  /**
   * Issue mà placement/baseline của phiên này không đo được (§13 Option C, edge case #15).
   *
   * Bơm từ ngoài (`resolveExcludedIssues(baseline)`) chứ không tự suy ra: `alert-core` không được
   * biết `CameraPlacement` là gì, nếu không thì thêm một placement là phải sửa cả engine cảnh báo.
   * Trên lý thuyết state machine đã không bao giờ phát event cho issue không đo được — nhưng nếu
   * điều đó bị vỡ ở đâu đó, ta muốn nó biến thành `ISSUE_UNAVAILABLE` nhìn thấy được trong Debug
   * Mode chứ không thành một câu nhắc sai gửi tới trẻ.
   */
  setUnavailableIssues(issues: Iterable<PostureIssueType>): void {
    this.unavailableIssues = new Set(issues);
  }

  decide(events: readonly PostureEvent[], now: number): AlertDecision {
    return this.select(events, now).decision;
  }

  async handle(events: readonly PostureEvent[], now: number): Promise<AlertDecision> {
    const { decision, event } = this.select(events, now);
    if (!decision.emit || event === undefined) return decision;

    this.lastAlertAt = now;
    this.lastIssueAt.set(event.type, now);
    this.history.push({
      issue: event.type,
      severity: event.severity,
      messageKey: decision.messageKey ?? "GENERIC",
      at: now,
      issueDuration: event.duration,
    });

    const key = decision.messageKey ?? "GENERIC";
    await this.announce(messageFor(key), key);
    return decision;
  }

  /**
   * Nói một câu không đến từ lỗi tư thế (nhắc nghỉ mắt §23).
   *
   * Đi qua đây thay vì gọi provider trực tiếp để nó cũng bị `muted` chi phối và cũng cập nhật
   * `lastAlertAt`: nhắc nghỉ mắt rồi nhắc tư thế ngay sau đó là hai giọng nói chồng lên nhau.
   */
  async speakMessage(key: AlertMessageKey, now: number): Promise<boolean> {
    if (this.muted) return false;
    this.lastAlertAt = now;
    await this.announce(messageFor(key), key);
    return true;
  }

  /** Bắt đầu phiên mới: xoá cooldown và bộ đếm, giữ nguyên `muted` (đó là lựa chọn của người dùng). */
  reset(): void {
    this.lastAlertAt = null;
    this.lastIssueAt.clear();
    this.history.length = 0;
  }

  /**
   * Luật §21, theo đúng thứ tự: chặn toàn cục trước, chọn issue sau.
   *
   * Thứ tự này quan trọng — nếu chọn issue trước rồi mới xét cooldown, `suppressedReason` sẽ báo
   * `PER_ISSUE_COOLDOWN` cho một tình huống mà thực ra mọi cảnh báo đều đang bị chặn, và người
   * tuning sẽ đi sửa sai chỗ.
   */
  private select(events: readonly PostureEvent[], now: number): Selection {
    if (events.length === 0) return { decision: NOTHING };
    if (this.muted) return { decision: { emit: false, suppressedReason: "MUTED" } };

    const suppressed: SuppressedAlert[] = [];
    const measurable = events.filter((event) => {
      if (!this.unavailableIssues.has(event.type)) return true;
      suppressed.push({ issue: event.type, reason: "ISSUE_UNAVAILABLE" });
      return false;
    });
    if (measurable.length === 0) {
      return { decision: { emit: false, suppressedReason: "ISSUE_UNAVAILABLE", suppressed } };
    }

    if (this.history.length >= this.config.maxAlertsPerSession) {
      return { decision: { emit: false, suppressedReason: "SESSION_CAP", suppressed } };
    }
    if (this.lastAlertAt !== null && now - this.lastAlertAt < this.config.cooldownMs) {
      return { decision: { emit: false, suppressedReason: "COOLDOWN", suppressed } };
    }

    // §21: severity cao nhất trước; trong cùng severity thì theo `severityPriority`.
    const candidates = [...measurable].sort((a, b) => {
      const bySeverity = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
      return bySeverity !== 0 ? bySeverity : this.priorityOf(a.type) - this.priorityOf(b.type);
    });

    for (const [index, event] of candidates.entries()) {
      const lastSame = this.lastIssueAt.get(event.type);
      if (lastSame !== undefined && now - lastSame < this.config.perIssueCooldownMs) {
        // Lỗi này vừa được nhắc → thử lỗi kế tiếp thay vì im lặng hoàn toàn. Cooldown toàn cục ở
        // trên đã bảo đảm tối đa một câu mỗi `cooldownMs`, nên đi tiếp không làm trẻ bị nhắc dồn;
        // ngược lại, dừng ở đây sẽ khiến một lỗi đang lặp lại che khuất mọi lỗi khác.
        suppressed.push({ issue: event.type, reason: "PER_ISSUE_COOLDOWN" });
        continue;
      }

      for (const rest of candidates.slice(index + 1)) {
        suppressed.push({ issue: rest.type, reason: "LOWER_PRIORITY" });
      }

      const messageKey = messageKeyFor(event.type);
      return {
        event,
        decision: {
          emit: true,
          issue: event.type,
          severity: event.severity,
          messageKey,
          message: messageFor(messageKey),
          suppressed,
        },
      };
    }

    return { decision: { emit: false, suppressedReason: "PER_ISSUE_COOLDOWN", suppressed } };
  }

  /** Issue không có trong `severityPriority` xếp cuối, không throw — config có thể do người dùng sửa. */
  private priorityOf(issue: PostureIssueType): number {
    const index = this.config.severityPriority.indexOf(issue);
    return index === -1 ? this.config.severityPriority.length : index;
  }

  /**
   * Phát ra thật. Lỗi của provider bị nuốt có chủ ý: TTS thất bại không được làm sập vòng lặp
   * inference đang chạy 12 lần/giây, và người dùng đã có băng chữ trên UI.
   */
  private async announce(message: string, key: AlertMessageKey): Promise<void> {
    try {
      this.provider.vibrate(this.config.vibrationPattern);
      await this.provider.chime();
      await this.provider.speak(message, key);
    } catch {
      // Im lặng — xem comment trên.
    }
  }
}
